import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {type InvoiceSettings, UpdateInvoiceSettingsRequest} from "@/api/enterprise-gateway-api.proxy.ts";
import * as yup from "yup";
import {useForm} from "vee-validate";
import {useModalEventsUtil} from "@/app/shared/helpers/modal-events-util.ts";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";

export const editInvoiceSettingsModal = 'edit-invoice-settings-modal';

export const invoiceSettingsValidationSchema = yup.object({
    invoiceReference: yup.string()
        .nullable()
        .max(35, 'Eigen referentie mag maximum 35 karakters bevatten'),
    forTheAttentionOf: yup.string().nullable()
});

interface InvoiceSettingsFormData {
    invoiceReference: string | null;
    forTheAttentionOf: string | null;
}

const FORM_INITIAL_VALUES: InvoiceSettingsFormData = {
    invoiceReference: null,
    forTheAttentionOf: null
};

export default defineComponent({
    name: 'AegEditInvoiceSettings',
    props: {
        invoiceSettings: {
            type: Object as PropType<InvoiceSettings>,
            required: true
        },
        modalId: {
            type: String,
            required: false,
            default: editInvoiceSettingsModal
        }
    },
    emits: ['on-save'],
    setup: function (props, {emit}) {
        const modalEventsUtil = useModalEventsUtil(props.modalId);
        const settings = ref<InvoiceSettings>(props.invoiceSettings);
        const apiProxy = useEnterpriseGatewayApiProxy();
        const isSaving = ref<boolean>(false);

        const {defineField, handleSubmit, errors, resetForm} = useForm<InvoiceSettingsFormData>({
            validationSchema: invoiceSettingsValidationSchema,
            initialValues: FORM_INITIAL_VALUES,
            validateOnMount: false,
            keepValuesOnUnmount: false,
        });

        const [invoiceReference] = defineField('invoiceReference');
        const [forTheAttentionOf] = defineField('forTheAttentionOf');

        const mapSettingsToFormData = (): InvoiceSettingsFormData => ({
            invoiceReference: settings.value.invoiceReference ?? null,
            forTheAttentionOf: settings.value.forTheAttentionOf ?? null
        });
        
        const title = computed(() => {
            return `Factuurinstellingen aanpassen`;
        });

        const settingsChanged = async (newSettings: InvoiceSettings) => {
            settings.value = newSettings;
            const formData = mapSettingsToFormData();
            resetForm({values: formData});
        };

        const handleFormSubmit = async () => {
            if (isSaving.value) return;
            
            isSaving.value = true;
            try {
                const updateRequest = UpdateInvoiceSettingsRequest.fromJS({
                    invoiceReference: invoiceReference.value,
                    forTheAttentionOf: forTheAttentionOf.value
                });
                
                const result = await apiProxy.invoiceSettings_Update(
                    settings.value.invoiceSettingsId!, 
                    updateRequest
                );
                
                emit('on-save', result);
                onCloseModal();
            } catch (error) {
                console.error('Failed to update invoice settings:', error);
            } finally {
                isSaving.value = false;
            }
        };

        const invalidForm = computed((): boolean =>  {
            return isSaving.value || Object.keys(errors.value).length > 0;
        });
        
        const onCloseModal = () => {
            modalEventsUtil.triggerModalToggle();
        };

        const onSubmit = handleSubmit(handleFormSubmit);
        
        watch(() => props.invoiceSettings, settingsChanged, {immediate: true, deep: true});

        return {
            modalId: props.modalId,
            invoiceReference,
            forTheAttentionOf,
            isSaving,
            invalidForm,
            title,
            settings,
            errors,
            onSubmit,
            handleFormSubmit,
            onCloseModal
        }
    }
});
